import { t } from '@reos/i18n';
import { DomainApiError } from './api-error.js';

/**
 * Krok NOC1 bod 2 (nocni QA 13. 10. 2026, nalez N01) — KVOTA NESMI SHODIT
 * OBRAZOVKU.
 *
 * ==== CO BYLO SPATNE ====
 *
 * `client_error_events` ma na `/smlouvy` (390×844, tri rozne verze) zapsanou
 * neodchycenou chybu „Příliš mnoho požadavků": prehled smluv nacita pri
 * otevreni seznam smluv, osnovu 20 smluv, podpisy, pozvanky, termíny, sablony
 * A JESTE cely index zakazek (`GET /v1/cases`, strankovane). Pri sweepu
 * obrazovek si tim jeden uzivatel vycerpa kvotu, API vrati 429 — a protoze
 * cast nacitani visela na `.then()` BEZ druhe vetve, skoncilo to neodchycenou
 * chybou a prazdnou obrazovkou „Obrazovku se nepodařilo zobrazit".
 *
 * ==== CO PLATI OD NOC1 ====
 *
 * Nacteni seznamu se pri 429 (a pri sitove chybe) OPAKUJE: cekani rika server
 * hlavickou `Retry-After`, a kdyz ji neposle, zdvojnasobuje se zaklad.
 * Teprve kdyz opakovani nepomuze, chyba se vraci volajicimu — ten ji ma
 * ukazat v OBSAHU obrazovky, nikdy ji nesmi nechat vybublat do error boundary.
 *
 * Sem patri jen CTENI (GET). Mutace se neopakuji: druhy pokus o POST je druha
 * objednavka, druha pozvanka, druhy podpis.
 */

/** Kolikrat se cteni zkusi znovu (prvni pokus se nepocita). */
const MAX_RETRIES = 2;

/** Zaklad cekani, kdyz server `Retry-After` neposle; druhy pokus je dvojnasobek. */
const BASE_DELAY_MS = 500;

/** Strop jednoho cekani — delsi pauza uz je pro cloveka u obrazovky zaseknuti. */
const MAX_DELAY_MS = 5_000;

export interface RetryOptions {
  readonly maxRetries?: number | undefined;
  readonly baseDelayMs?: number | undefined;
  /** Vlastni cekani (sady je nahrazuji, aby nemusely cekat skutecny cas). */
  readonly sleep?: ((ms: number) => Promise<void>) | undefined;
}

/**
 * PRETRZENE SPOJENI: `fetch` vyhodil `TypeError` („Failed to fetch") a zadna
 * odpoved serveru nedosla. Odpoved se stavem (`DomainApiError`, tedy i 4xx
 * a 5xx) to NENI — ta uz je rozhodnuti serveru.
 */
export function isTornConnectionError(error: unknown): boolean {
  if (error instanceof DomainApiError) return false;
  return error instanceof TypeError;
}

/** 429 = kvota; sitova chyba (`TypeError` z `fetch`) = vypadek cesty k serveru. */
export function isRetryableLoadError(error: unknown): boolean {
  if (error instanceof DomainApiError) return error.status === 429;
  return isTornConnectionError(error);
}

function delayFor(error: unknown, attempt: number, baseDelayMs: number): number {
  const fromServer = error instanceof DomainApiError ? error.retryAfterSeconds : undefined;
  const wanted = fromServer === undefined ? baseDelayMs * 2 ** attempt : fromServer * 1_000;
  return Math.min(wanted, MAX_DELAY_MS);
}

const sleepReal = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Spusti cteni a pri kvote nebo sitove chybe ho zkusi znovu. Vraci posledni
 * chybu, kdyz ani opakovani neuspelo — volajici na ni MUSI reagovat hlaskou.
 */
export async function retryOnRateLimit<T>(
  load: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const maxRetries = options.maxRetries ?? MAX_RETRIES;
  const baseDelayMs = options.baseDelayMs ?? BASE_DELAY_MS;
  const sleep = options.sleep ?? sleepReal;
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await load();
    } catch (error) {
      if (attempt >= maxRetries || !isRetryableLoadError(error)) throw error;
      await sleep(delayFor(error, attempt, baseDelayMs));
    }
  }
}

/**
 * MUTACE, KTERE SE PRETRHLO SPOJENI (krok TOKYFIX5 bod 2).
 *
 * ==== CO BYLO SPATNE ====
 *
 * Mereni P6/desktop 29. 10. 2026 8:34:14 UTC: `POST /v1/contract-signatures`
 * skoncil `net::ERR_NETWORK_CHANGED` (nginx zapsal 499, v API logu neni nic —
 * na stroji se v te sekunde zmenila sit, viz tyz vzorec v `downloadServerPdf`,
 * krok QAFIX7). Makleřka na to dostala „Akce se nezdařila. Zkuste to znovu."
 * a podepisovani nezacalo, prestoze server byl v poradku.
 *
 * ==== PROC NE PROSTE „ZKUSIT ZNOVU" ====
 *
 * 499 znamena, ze pozadavek k serveru DOSEL a on ho mohl provest — klient se
 * jen nedockal odpovedi. Slepe opakovani POSTu je proto druhe podepisovani,
 * druha pozvanka, druhy podpis (presne to, co zakazuje hlavicka tohoto
 * souboru). Druhy pokus smi prijit jen tehdy, kdyz se SAMOSTATNYM CTENIM
 * overi, ze se prvni NEPROVEDL:
 *
 *   * `happened()` rekne `true`  → prvni pokus prosel, mutace je hotova,
 *   * `happened()` rekne `false` → prvni pokus nedosel, jde druhy pokus,
 *   * `happened()` selze         → nevime; vraci se puvodni chyba a rozhodne
 *                                  clovek (radsi hlaska nez duplikat).
 */
export async function retryTornMutation(
  mutate: () => Promise<unknown>,
  happened: () => Promise<boolean>,
): Promise<void> {
  try {
    await mutate();
    return;
  } catch (error) {
    if (!isTornConnectionError(error)) throw error;
    const done = await happened().then(
      (hotovo) => hotovo,
      () => null,
    );
    if (done === true) return;
    if (done === null) throw error;
    await mutate();
  }
}

/**
 * Hlaska pro cloveka, kdyz ani opakovani nepomohlo. Kvota je jiny pribeh nez
 * „nepodarilo se nacist": uzivatel ma vedet, ze staci chvili pockat.
 */
export function rateLimitMessage(error: unknown): string | undefined {
  if (!(error instanceof DomainApiError) || error.status !== 429) return undefined;
  return t('webAdmin.rateLimit.listRetryLater', { locale: 'cs' });
}
